import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { STAGES } from "@/lib/constants";
import StageSelector from "@/components/StageSelector";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Pencil, Loader2 } from "lucide-react";

export default function EditJobDialog({ job }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [ms, setMs] = useState(job.ms_description || "");
  const [clientId, setClientId] = useState(job.client_id || "");
  const [captainId, setCaptainId] = useState(job.assigned_captain_id || "");
  const [target, setTarget] = useState(job.target_completion_date || "");
  const [actual, setActual] = useState(job.actual_completion_date || "");
  const stageNames = job.stages.map((s) => s.name);
  const [selected, setSelected] = useState(stageNames.filter((n) => STAGES.includes(n)));
  const [custom, setCustom] = useState(stageNames.filter((n) => !STAGES.includes(n)));

  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data, enabled: open });
  const { data: captains = [] } = useQuery({ queryKey: ["captains"], queryFn: async () => (await api.get("/captains")).data, enabled: open });

  const mutation = useMutation({
    mutationFn: async (payload) => (await api.put(`/jobs/${job.id}`, payload)).data,
    onSuccess: () => {
      toast.success("Job updated");
      qc.invalidateQueries({ queryKey: ["job", job.id] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
      setOpen(false);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const submit = (e) => {
    e.preventDefault();
    mutation.mutate({
      ms_description: ms,
      client_id: clientId || undefined,
      assigned_captain_id: captainId || null,
      selected_stages: selected,
      custom_stages: custom,
      target_completion_date: target || null,
      actual_completion_date: actual || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="edit-job-button"><Pencil className="h-4 w-4 mr-2" /> Edit</Button>
      </DialogTrigger>
      <DialogContent data-testid="edit-job-dialog" className="max-h-[90vh] overflow-y-auto scrollbar-thin">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit {job.job_number}</DialogTitle>
          <DialogDescription>Update the method statement, client, captain, works, and completion dates.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Method Statement description</Label>
            <Textarea value={ms} onChange={(e) => setMs(e.target.value)} required rows={3} className="mt-1.5" data-testid="edit-ms-input" />
          </div>
          <div>
            <Label>Client</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="mt-1.5" data-testid="edit-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Assigned Captain</Label>
            <Select value={captainId} onValueChange={setCaptainId}>
              <SelectTrigger className="mt-1.5" data-testid="edit-captain-select"><SelectValue placeholder="Select captain" /></SelectTrigger>
              <SelectContent>
                {captains.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <StageSelector selected={selected} setSelected={setSelected} custom={custom} setCustom={setCustom} />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Target completion</Label>
              <Input type="date" value={target} onChange={(e) => setTarget(e.target.value)} className="mt-1.5" data-testid="edit-target-date" />
            </div>
            <div>
              <Label>Actual finished date</Label>
              <Input type="date" value={actual} onChange={(e) => setActual(e.target.value)} className="mt-1.5" data-testid="edit-actual-date" />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending} data-testid="save-job-button">
              {mutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
